import type {
  AuthConfig,
  TranscriptMeta,
  TranscriptDoc,
  BackupFile,
  InvitePreview,
  JoinPreview,
  PasswordResetPreview,
  RecordingDetail,
  RecordingSummary,
  Render,
  Studio,
  TeamInvite,
  TeamMember,
} from '@greenroom/shared'
import { queryOptions } from '@tanstack/react-query'
import { api } from './api'

/** Every server read in one place, so keys stay consistent between the page that reads and the mutation that updates. */
export const q = {
  transcripts: (recordingId: string) => queryOptions({ queryKey: ['recordings', recordingId, 'transcripts'], queryFn: () => api<(TranscriptMeta & { wordsUrl: string })[]>(`/api/recordings/${recordingId}/transcripts`), staleTime: 0, refetchOnMount: 'always', refetchInterval: 5000 }),
  transcriptWords: (url: string) => queryOptions({ queryKey: ['transcript-words', url], queryFn: () => api<TranscriptDoc>(url), staleTime: Infinity }),
  studios: (teamId: string) =>
    queryOptions({ queryKey: ['teams', teamId, 'studios'], queryFn: () => api<Studio[]>(`/api/teams/${teamId}/studios`), refetchInterval: 15_000 }),
  studio: (studioId: string) =>
    queryOptions({ queryKey: ['studios', studioId], queryFn: () => api<Studio>(`/api/studios/${studioId}`), refetchInterval: 15_000 }),
  recordings: (teamId: string, studioId?: string) =>
    queryOptions({
      queryKey: ['teams', teamId, 'recordings', studioId ?? 'all'],
      queryFn: () => api<RecordingSummary[]>(`/api/teams/${teamId}/recordings${studioId ? `?studioId=${encodeURIComponent(studioId)}` : ''}`),
      // media keeps processing for a while after a session; the list shows it moving
      refetchInterval: (query) => (query.state.data?.some((r) => r.mediaStatus === 'waiting' || r.mediaStatus === 'processing' || r.status !== 'ended') ? 5_000 : 30_000),
    }),
  recording: (recordingId: string) =>
    queryOptions({
      queryKey: ['recordings', recordingId],
      queryFn: () => api<RecordingDetail>(`/api/recordings/${recordingId}`),
      refetchInterval: (query) => {
        const r = query.state.data
        if (!r) return false
        const busy = !!r.import && (r.import.status === 'draft' || r.import.files.some((f) => f.role === 'source' && !f.alignment && f.status !== 'failed')) || !!r.transferBusyReason || r.status !== 'ended' || r.mediaStatus === 'waiting' || r.mediaStatus === 'processing' || r.media.some((m) => m.status === 'waiting' || m.status === 'processing')
        return busy ? 3_000 : false
      },
    }),
  backups: (recordingId: string) =>
    queryOptions({ queryKey: ['recordings', recordingId, 'backups'], queryFn: () => api<BackupFile[]>(`/api/recordings/${recordingId}/backups`) }),
  members: (teamId: string) => queryOptions({ queryKey: ['teams', teamId, 'members'], queryFn: () => api<TeamMember[]>(`/api/teams/${teamId}/members`) }),
  invites: (teamId: string) => queryOptions({ queryKey: ['teams', teamId, 'invites'], queryFn: () => api<TeamInvite[]>(`/api/teams/${teamId}/invites`) }),
  authConfig: queryOptions({ queryKey: ['auth-config'], queryFn: () => api<AuthConfig>('/api/auth/config'), staleTime: Infinity }),
  invite: (token: string) => queryOptions({ queryKey: ['invite', token], queryFn: () => api<InvitePreview>(`/api/invites/${token}`), retry: false }),
  passwordReset: (token: string) =>
    queryOptions({ queryKey: ['password-reset', token], queryFn: () => api<PasswordResetPreview>(`/api/password-resets/${token}`), retry: false }),
  joinPreview: (token: string) => queryOptions({ queryKey: ['join', token], queryFn: () => api<JoinPreview>(`/api/join/${token}`), retry: false }),
  renders: (projectId: string) =>
    queryOptions({
      queryKey: ['projects', projectId, 'renders'],
      queryFn: () => api<Render[]>(`/api/projects/${projectId}/renders`),
      refetchInterval: (query) => (query.state.data?.some((r) => r.status === 'queued' || r.status === 'rendering') ? 1_500 : false),
    }),
}
